import React, { useContext, useState, useMemo } from 'react';
import * as Sentry from '@sentry/react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import {
  ButtonContainer,
  Container,
  InputContainer,
  InputContent,
  Table,
  TableBody,
  TableBodyNobody,
  TableBodySkeleton,
  TableHeader,
} from './styles';
import ActionButtonIcon from '~/componentes/ActionButtonIcon';
import { pdf } from '@react-pdf/renderer';
import GmudPdfTemplatePDF from '~/componentes/TemplatePDF/Gmud';
import Pagination from '~/componentes/Pagination';
import InputSelect from '~/componentes/Inputs/InputSelect';
import InputDate from '~/componentes/Inputs/InputDate';
import InputText from '~/componentes/Inputs/InputText';
import sistemaListStore, {
  sistemaListEditShowModal,
  sistemaListShowModal,
} from '~/pages/Sistema/componentes/SistemaList/ThisMainComponent/thisStore';
import ThisModalButtonHandler from '~/pages/Sistema/componentes/SistemaList/ThisMainComponent/ThisModalButtonHandler';
import { StatusCard } from '~/componentes/StatusCard';
import { StatusBadge } from '~/componentes/StatusBadge';
import formatDateToSaoPauloTimezone from '~/utils/formatDateToSaoPauloTimezone';
import { Colors } from '~/config/Colors';

function CustomBodyContent({
  data,
  loadedData,
  clientes,
  perfilAtual,
  analistas,
  onCreateFromTemplate,
}) {
  const [pageData, setPageData] = useState([]);
  const [generatePdfLoading, setGeneratePdfLoading] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    cliente: '',
    analista: '',
    dataInicio: '',
  });

  const isDarkTheme = localStorage.getItem('tema') == 'E';

  const { dispatch } = useContext(sistemaListStore);

  const clearAllFilters = () => {
    setFilters({
      search: '',
      status: '',
      cliente: '',
      analista: '',
      dataInicio: '',
    });
  };

  const updateFilter = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  function formatDate(dateString) {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date)) return '';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  const clientesOptions = clientes.map(item => ({
    value: String(item?.id),
    label: item?.nomeFantasia,
  }));

  const analistasOptions = useMemo(() => {
    return analistas.map(item => ({
      value: String(item?.perfil?.usuario?.id),
      label: item?.nome,
    }));
  }, [analistas]);

  const statusOptions = [
    { value: '', label: 'Todos os status' },
    { value: 'pendente', label: 'Pendente' },
    { value: 'concluida', label: 'Concluída' },
    { value: 'cancelada', label: 'Cancelada' },
  ];

  const calculateStatusCounts = () => {
    const total = data.length;
    const pendentes = data.filter(item => item?.status === 'pendente').length;
    const concluidas = data.filter(item => item?.status === 'concluida').length;
    const canceladas = data.filter(item => item?.status === 'cancelada').length;

    return { total, pendentes, concluidas, canceladas };
  };

  const filterGmudItem = item => {
    if (!item?.id) return true;

    const searchMatch =
      !filters.search ||
      String(item?.id).includes(filters.search) ||
      item?.titulo?.toLowerCase().includes(filters.search.toLowerCase());

    const statusMatch = !filters.status || item?.status === filters.status;
    const clienteMatch = !filters.cliente || String(item?.clienteGmud?.id) === filters.cliente;
    const analistaMatch = !filters.analista || item?.executorGmud?.id === Number(filters.analista);

    let dataMatch = true;
    if (filters.dataInicio) {
      const filterDate = new Date(filters.dataInicio).toDateString();
      const itemDate = new Date(item?.dataHoraInicio).toDateString();
      dataMatch = filterDate === itemDate;
    }

    return searchMatch && statusMatch && clienteMatch && analistaMatch && dataMatch;
  };

  const filteredData = useMemo(() => {
    return data.filter(filterGmudItem);
  }, [data, filters]);

  const filteredPageData = pageData.filter(filterGmudItem);

  const filledPageData =
    filteredPageData.length < 15
      ? [
          ...filteredPageData,
          ...Array.from({ length: 15 - filteredPageData.length }, () => ({
            id: '',
            titulo: '',
            solicitante: '',
            dataSolicitacao: '',
            dataHoraInicio: '',
            dataHoraFim: '',
            status: '',
          })),
        ]
      : filteredPageData;

  const exportPdf = async gmudData => {
    setGeneratePdfLoading(true);

    try {
      const formattedGmudData = {
        ...gmudData,
        dataHoraInicio: formatDateToSaoPauloTimezone(gmudData?.dataHoraInicio),
        dataHoraFim: formatDateToSaoPauloTimezone(gmudData?.dataHoraFim),
        dataSolicitacao: formatDateToSaoPauloTimezone(gmudData?.dataSolicitacao),
        execucao: gmudData?.execucao?.map(execucaoItem => ({
          ...execucaoItem,
          dataHoraInicio: formatDateToSaoPauloTimezone(execucaoItem?.dataHoraInicio),
        })),
        rollback: gmudData?.rollback?.map(rollbackItem => ({
          ...rollbackItem,
          datahora: formatDateToSaoPauloTimezone(rollbackItem?.datahora),
        })),
      };

      const blob = await pdf(
        <GmudPdfTemplatePDF key={Date.now()} {...formattedGmudData} />,
      ).toBlob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      Sentry.captureException(error);
      console.error('Erro ao gerar PDF da GMUD:', error);
    } finally {
      setTimeout(() => {
        setGeneratePdfLoading(false);
      }, 1000);
    }
  };

  return (
    <Container>
      <div
        style={{
          display: 'flex',
          gap: '16px',
          width: '100%',
        }}
      >
        <StatusCard
          title="Total de GMUDs"
          value={calculateStatusCounts().total}
          icon="list_alt"
          color={isDarkTheme ? '#ffffff' : '#6796cf'}
          loading={loadedData}
        />
        <StatusCard
          title="Pendentes"
          value={calculateStatusCounts().pendentes}
          icon="schedule"
          color="#FFA500"
          loading={loadedData}
        />
        <StatusCard
          title="Concluídas"
          value={calculateStatusCounts().concluidas}
          icon="check_circle"
          color="#4CAF50"
          loading={loadedData}
        />
        <StatusCard
          title="Canceladas"
          value={calculateStatusCounts().canceladas}
          icon="cancel"
          color="#F44336"
          loading={loadedData}
        />
      </div>

      <InputContainer>
        <h4
          style={{
            margin: '0 0 8px 0',
            color: isDarkTheme ? '#fff' : '#6796cf',
            fontSize: '1rem',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <i
            className="material-icons"
            style={{
              fontSize: '16px',
              color: isDarkTheme ? '#fff' : '#6796cf',
            }}
          >
            filter_alt
          </i>
          Filtros de Pesquisa
        </h4>

        <InputContent>
          <div>
            <label
              style={{
                display: 'block',
                marginBottom: '4px',
                fontSize: '0.8rem',
                color: 'rgba(255, 255, 255, 0.7)',
                fontWeight: '400',
              }}
            >
              Buscar por ID ou Título
            </label>
            <InputText
              placeholder="Digite o ID ou título da GMUD..."
              value={filters.search}
              onChange={e => updateFilter('search', e.target.value)}
            />
          </div>

          <div>
            <label
              style={{
                display: 'block',
                marginBottom: '4px',
                fontSize: '0.8rem',
                color: 'rgba(255, 255, 255, 0.7)',
                fontWeight: '400',
              }}
            >
              Status
            </label>
            <InputSelect
              options={statusOptions}
              placeholder="Selecione o status"
              value={filters.status}
              onChange={e => updateFilter('status', e.target.value)}
              isClearable={false}
            />
          </div>

          <div>
            <label
              style={{
                display: 'block',
                marginBottom: '4px',
                fontSize: '0.8rem',
                color: 'rgba(255, 255, 255, 0.7)',
                fontWeight: '400',
              }}
            >
              Clientes
            </label>
            <InputSelect
              options={[{ value: '', label: 'Todos os clientes' }, ...clientesOptions]}
              placeholder="Selecione o cliente"
              value={filters.cliente}
              onChange={e => updateFilter('cliente', e.target.value)}
            />
          </div>

          <div>
            <label
              style={{
                display: 'block',
                marginBottom: '4px',
                fontSize: '0.8rem',
                color: 'rgba(255, 255, 255, 0.7)',
                fontWeight: '400',
              }}
            >
              Analista
            </label>
            <InputSelect
              options={[{ value: '', label: 'Todos os analistas' }, ...analistasOptions]}
              placeholder="Selecione o analista"
              value={filters.analista}
              onChange={e => updateFilter('analista', e.target.value)}
            />
          </div>

          <div>
            <label
              style={{
                display: 'block',
                marginBottom: '4px',
                fontSize: '0.8rem',
                color: 'rgba(255, 255, 255, 0.7)',
                fontWeight: '400',
              }}
            >
              Data de Início
            </label>
            <InputDate
              placeholder="Selecione a data"
              value={filters.dataInicio}
              onChange={date => updateFilter('dataInicio', date)}
            />
          </div>

          <button
            onClick={clearAllFilters}
            style={{
              padding: '8px',
              background: Colors.Input_Color_1,
              border: 'none',
              borderRadius: '8px',
              color: Colors.text_Color_1,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '0.85rem',
              transition: 'all 0.3s ease',
              height: '36px',
            }}
            onMouseEnter={e => {
              e.target.style.background = `${Colors.Input_Color_1}90`;
            }}
            onMouseLeave={e => {
              e.target.style.background = Colors.Input_Color_1;
            }}
            title="Limpar Filtros"
          >
            <i className="material-icons" style={{ fontSize: '18px' }}>
              filter_list_off
            </i>
          </button>
        </InputContent>
      </InputContainer>

      <Table>
        <TableHeader>
          <div>ID</div>
          <div>Título</div>
          <div>Status</div>
          <div>Cliente</div>
          <div>Analista</div>
          <div>Data de Solicitação</div>
          <div>Data de Início</div>
          <div>Ações</div>
        </TableHeader>
        {loadedData ? (
          Array.from({ length: 5 }).map((_, idx) => (
            <TableBodySkeleton key={idx}>
              <div className="skeleton" style={{ height: 20, width: 40 }} />
              <div className="skeleton" style={{ height: 20, width: 220 }} />
              <div className="skeleton" style={{ height: 20, width: 100 }} />
              <div className="skeleton" style={{ height: 20, width: 120 }} />
              <div className="skeleton" style={{ height: 20, width: 120 }} />
              <div className="skeleton" style={{ height: 20, width: 120 }} />
              <div className="skeleton" style={{ height: 20, width: 120 }} />
              <ButtonContainer>
                <div className="skeleton" style={{ height: 32, width: 32, borderRadius: 16 }} />
                <div className="skeleton" style={{ height: 32, width: 32, borderRadius: 16 }} />
                <div className="skeleton" style={{ height: 32, width: 32, borderRadius: 16 }} />
              </ButtonContainer>
            </TableBodySkeleton>
          ))
        ) : pageData.length > 0 ? (
          filledPageData.map((item, index) => (
            <TableBody key={index} hasBorder={!!item?.id}>
              <div>{item?.id}</div>
              <div>{item?.titulo}</div>
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                {item?.status === 'pendente' && (
                  <StatusBadge
                    icon="schedule"
                    color="#FFA500"
                    backgroundColor="rgba(255, 165, 0, 0.1)"
                    text="Pendente"
                  />
                )}
                {item?.status === 'concluida' && (
                  <StatusBadge
                    icon="check_circle"
                    color="#4CAF50"
                    backgroundColor="rgba(76, 175, 80, 0.1)"
                    text="Concluída"
                  />
                )}
                {item?.status === 'cancelada' && (
                  <StatusBadge
                    icon="cancel"
                    color="#F44336"
                    backgroundColor="rgba(244, 67, 54, 0.1)"
                    text="Cancelada"
                  />
                )}
              </div>
              <div>{item?.clienteGmud?.nomeFantasia}</div>
              <div>{item?.executorGmud?.nome || ''}</div>
              <div>{formatDate(item?.dataSolicitacao ?? '')}</div>
              <div>{formatDate(item?.dataHoraInicio ?? '')}</div>
              {item?.id ? (
                <ButtonContainer>
                  <ActionButtonIcon
                    icon="edit"
                    onClick={e => {
                      e.stopPropagation();

                      if (item?.idExecutor !== perfilAtual?.perfil?.usuario?.id) {
                        toast.error('Seu usuário não pode editar essa GMUD', {
                          position: 'bottom-left',
                          autoClose: 3000,
                          hideProgressBar: false,
                          closeOnClick: true,
                          pauseOnHover: true,
                          draggable: true,
                          progress: undefined,
                          style: {
                            width: '400px',
                            fontSize: '0.85rem',
                            background: isDarkTheme ? '#2d2d2d' : '#fff',
                            color: isDarkTheme ? '#fff' : '#333',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                            borderRadius: '8px',
                          },
                        });
                        return;
                      }

                      if (item?.status === 'concluida' || item?.status === 'cancelada') {
                        toast.info('GMUD com status concluída ou cancelada não pode ser editada', {
                          position: 'bottom-left',
                          autoClose: 3000,
                          hideProgressBar: false,
                          closeOnClick: true,
                          pauseOnHover: true,
                          draggable: true,
                          progress: undefined,
                          style: {
                            width: '400px',
                            fontSize: '0.85rem',
                            background: isDarkTheme ? '#2d2d2d' : '#fff',
                            color: isDarkTheme ? '#fff' : '#333',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                            borderRadius: '8px',
                          },
                        });
                        return;
                      }

                      dispatch(sistemaListShowModal({ edit: true }, { ...item, idDelete: 0 }));
                      dispatch(sistemaListEditShowModal({ edit: true, editShow: true }));
                    }}
                    toltipText="Editar GMUD"
                    isDisabled={
                      item?.status === 'concluida' ||
                      item?.status === 'cancelada' ||
                      item?.idExecutor !== perfilAtual?.perfil?.usuario?.id
                    }
                  />

                  <ActionButtonIcon
                    icon="description"
                    onClick={e => {
                      e.stopPropagation();

                      if (item?.status === 'cancelada') {
                        toast.info('GMUD com status cancelada não pode ser exportada', {
                          position: 'bottom-left',
                          autoClose: 3000,
                          hideProgressBar: false,
                          closeOnClick: true,
                          pauseOnHover: true,
                          draggable: true,
                          progress: undefined,
                          style: {
                            width: '400px',
                            fontSize: '0.85rem',
                            background: isDarkTheme ? '#2d2d2d' : '#fff',
                            color: isDarkTheme ? '#fff' : '#333',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                            borderRadius: '8px',
                          },
                        });
                        return;
                      }
                      exportPdf(item);
                    }}
                    toltipText={'Exportar documento'}
                    loading={generatePdfLoading}
                  />

                  <button
                    className="btn"
                    onClick={e => {
                      e.stopPropagation();
                      onCreateFromTemplate(item);
                    }}
                  >
                    <ThisModalButtonHandler moldalType="create" className="btn_circle" small>
                      <i className="material-icons">difference</i>
                    </ThisModalButtonHandler>
                  </button>
                </ButtonContainer>
              ) : (
                <div>&nbsp;</div>
              )}
            </TableBody>
          ))
        ) : (
          <TableBodyNobody>
            <div></div>
            <div></div>
            <div></div>
            <div></div>
            <div>Nenhuma GMUD cadastrada no momento.</div>
            <div></div>
            <div></div>
            <div></div>
          </TableBodyNobody>
        )}
      </Table>

      <Pagination data={filteredData} size={15} getInfo={({ pageData }) => setPageData(pageData)} />
      <ToastContainer />
    </Container>
  );
}

export default CustomBodyContent;
